/**
 * RBAC-3.2 · Create, rename and delete a role the account owns.
 *
 * ⚠️ THIS MODULE HOLDS NO RULES. Who may, which role, the name rule and what blocks a delete are all
 * `20261101b3` (and RBAC-1.1's name guard). What the answers mean is `lib/roleLifecycle.ts`. The
 * account is the caller's current one, decided server-side; nothing here passes a tenant id.
 *
 * Errors are rethrown as `RoleLifecycleError`, carrying the server's code and, for a refused
 * delete or rename (55006), what holds the role.
 */

import { supabase } from '@/lib/supabaseClient';
import {
  CREATE_ACCOUNT_ROLE_RPC,
  DELETE_ACCOUNT_ROLE_RPC,
  RENAME_ACCOUNT_ROLE_RPC,
  ROLE_DELETE_CHECK_RPC,
  holdersFromError,
  parseDeleteCheck,
  parseRoleWriteResult,
  roleLifecycleErrorMessage,
  type RoleDeleteCheck,
  type RoleHolder,
  type RoleWriteResult,
  type RpcErrorLike,
} from '@/lib/roleLifecycle';

export class RoleLifecycleError extends Error {
  readonly code: string | null;
  readonly holders: RoleHolder[];
  constructor(err: RpcErrorLike | null, fallback: string) {
    super(roleLifecycleErrorMessage(err, fallback));
    this.name = 'RoleLifecycleError';
    this.code = err?.code ?? null;
    this.holders = holdersFromError(err);
  }
}

const UNREADABLE = 'The change was made, but the reply was not understood. Reload to see it.';

export async function createAccountRole(name: string, description: string): Promise<RoleWriteResult> {
  const { data, error } = await supabase.rpc(CREATE_ACCOUNT_ROLE_RPC, { p_name: name, p_description: description });
  if (error) throw new RoleLifecycleError(error, 'The role could not be created.');
  const result = parseRoleWriteResult(data);
  if (!result) throw new RoleLifecycleError({ message: UNREADABLE }, UNREADABLE);
  return result;
}

export async function renameAccountRole(roleId: number, name: string, description: string): Promise<RoleWriteResult> {
  const { data, error } = await supabase.rpc(RENAME_ACCOUNT_ROLE_RPC, {
    p_role_id: roleId,
    p_name: name,
    p_description: description,
  });
  if (error) throw new RoleLifecycleError(error, 'The role could not be saved.');
  const result = parseRoleWriteResult(data);
  if (!result) throw new RoleLifecycleError({ message: UNREADABLE }, UNREADABLE);
  return result;
}

export async function fetchRoleDeleteCheck(roleId: number): Promise<RoleDeleteCheck> {
  const { data, error } = await supabase.rpc(ROLE_DELETE_CHECK_RPC, { p_role_id: roleId });
  if (error) throw new RoleLifecycleError(error, 'The role could not be checked.');
  const check = parseDeleteCheck(data);
  if (!check) throw new RoleLifecycleError({ message: 'The check came back in a shape this screen does not understand.' }, '');
  return check;
}

export async function deleteAccountRole(roleId: number): Promise<void> {
  const { error } = await supabase.rpc(DELETE_ACCOUNT_ROLE_RPC, { p_role_id: roleId });
  if (error) throw new RoleLifecycleError(error, 'The role could not be deleted.');
}
